import { useEffect, useMemo, useState, type ReactNode } from "react";
import { onIdTokenChanged, type User } from "firebase/auth";
import { getFirebaseAuth } from "./firebase";
import { AuthContext, type AuthState } from "./AuthContext";
import { getConfig } from "@/config";

// Synthetic user used when devAuthBypass is on. Shaped just enough to satisfy
// RequireAuth + the Layout caption; never passed to a real Firebase API.
const DEV_USER = {
  uid: "dev-bypass",
  email: "dev@local",
  displayName: "Dev (bypass)",
  getIdToken: async () => null,
} as unknown as User;

export function AuthProvider({ children }: { children: ReactNode }) {
  const bypass = getConfig().devAuthBypass === true;
  const [user, setUser] = useState<User | null>(bypass ? DEV_USER : null);
  const [loading, setLoading] = useState(!bypass);

  useEffect(() => {
    if (bypass) return;
    const auth = getFirebaseAuth();
    return onIdTokenChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
  }, [bypass]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      getIdToken: async () => {
        if (bypass) return null;
        return user ? await user.getIdToken() : null;
      },
    }),
    [user, loading, bypass],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
